/**
 * LeaveUI — shared design primitives for the leave module.
 * Import from here for consistent button, badge, skeleton, and empty-state styles.
 */

import { Link } from "react-router-dom";
import { ArrowLeft, LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

// ─── Token constants ──────────────────────────────────────────────────────────

export const btn = {
  base: "inline-flex items-center gap-2 rounded-xl text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  primary:
    "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 active:scale-[0.98] focus-visible:ring-emerald-500",
  secondary:
    "bg-white border border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 active:scale-[0.98] focus-visible:ring-slate-400",
  ghost:
    "text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-slate-400",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 active:scale-[0.98] focus-visible:ring-red-500",
  dangerOutline:
    "border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 active:scale-[0.98] focus-visible:ring-red-400",
  amber:
    "bg-amber-500 text-white shadow-sm hover:bg-amber-600 active:scale-[0.98] focus-visible:ring-amber-400",
  sm: "px-3 py-1.5",
  md: "px-4 py-2",
  lg: "px-5 py-2.5",
  icon: "p-2",
} as const;

/** Compose a button className from token parts */
export function btnCls(
  variant: "primary" | "secondary" | "ghost" | "danger" | "dangerOutline" | "amber",
  size: "sm" | "md" | "lg" | "icon" = "md",
  extra?: string,
) {
  return [btn.base, btn[variant], btn[size], extra].filter(Boolean).join(" ");
}

// ─── Button component ─────────────────────────────────────────────────────────

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "dangerOutline" | "amber";
  size?: "sm" | "md" | "lg" | "icon";
  icon?: LucideIcon;
  loading?: boolean;
  loadingText?: string;
};

export function Btn({
  variant = "secondary",
  size = "md",
  icon: Icon,
  loading,
  loadingText,
  children,
  className,
  disabled,
  ...rest
}: BtnProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={btnCls(variant, size, className)}
    >
      {loading ? (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : Icon ? (
        <Icon className="h-4 w-4 shrink-0" />
      ) : null}
      {loading ? (loadingText ?? "Loading…") : children}
    </button>
  );
}

// ─── Link button ──────────────────────────────────────────────────────────────

type LinkBtnProps = {
  to: string;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  icon?: LucideIcon;
  children: React.ReactNode;
  className?: string;
};

export function LinkBtn({ to, variant = "secondary", size = "md", icon: Icon, children, className }: LinkBtnProps) {
  return (
    <Link to={to} className={btnCls(variant, size, className)}>
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {children}
    </Link>
  );
}

// ─── Back button ─────────────────────────────────────────────────────────────

export function BackBtn({ to, label = "Back" }: { to?: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <Btn
      variant="secondary"
      size="md"
      icon={ArrowLeft}
      onClick={() => (to ? navigate(to) : navigate(-1))}
    >
      {label}
    </Btn>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  { dot: string; text: string; bg: string; label: string }
> = {
  APPROVED: {
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    label: "Approved",
  },
  REJECTED: {
    dot: "bg-red-500",
    text: "text-red-700",
    bg: "bg-red-50 border-red-200",
    label: "Rejected",
  },
  CANCELLED: {
    dot: "bg-slate-400",
    text: "text-slate-600",
    bg: "bg-slate-100 border-slate-200",
    label: "Cancelled",
  },
  PENDING_APPROVAL: {
    dot: "bg-amber-400",
    text: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    label: "Pending Approval",
  },
  DRAFT: {
    dot: "bg-slate-300",
    text: "text-slate-500",
    bg: "bg-slate-50 border-slate-200",
    label: "Draft",
  },
};

export function StatusBadge({ value }: { value?: string | null }) {
  const cfg = STATUS_CONFIG[value ?? ""] ?? {
    dot: "bg-slate-300",
    text: "text-slate-500",
    bg: "bg-slate-50 border-slate-200",
    label: value?.replaceAll("_", " ") ?? "—",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-lg bg-slate-200 ${className ?? ""}`} />
  );
}

export function CardSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className="border-b bg-slate-50 px-5 py-4">
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="ml-auto h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-9 w-28 rounded-xl" />
      </div>
      <CardSkeleton rows={5} />
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
        <Icon className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="mt-4 font-semibold text-slate-700">{title}</h3>
      {description && (
        <p className="mt-1 max-w-xs text-sm text-slate-400">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  noPad,
}: {
  children: React.ReactNode;
  className?: string;
  noPad?: boolean;
}) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${noPad ? "" : "p-5"} ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
      <div>
        <h2 className="font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Table primitives ─────────────────────────────────────────────────────────

export function Table({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${className ?? ""}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">{children}</table>
      </div>
    </div>
  );
}

export function THead({ children }: { children: React.ReactNode }) {
  return (
    <thead className="border-b bg-slate-50">
      <tr>{children}</tr>
    </thead>
  );
}

export function TH({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 ${right ? "text-right" : ""}`}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: React.ReactNode }) {
  return <tbody className="divide-y divide-slate-100">{children}</tbody>;
}

export function TR({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <tr className={`transition-colors hover:bg-slate-50/60 ${className ?? ""}`}>
      {children}
    </tr>
  );
}

export function TD({
  children,
  className,
  right,
  muted,
}: {
  children?: React.ReactNode;
  className?: string;
  right?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      className={`px-4 py-3.5 ${right ? "text-right" : ""} ${muted ? "text-slate-400" : ""} ${className ?? ""}`}
    >
      {children}
    </td>
  );
}
